import { ATSConnectHiringRequestCandidateFeedbackTestIds } from 'data-testids/ATS';
import { CandidateFeedbackRow } from '../FeedbackDetail/components/CandidateFeedbackRow';
import { FeedbackListPaneHeader } from './components/FeedbackListPaneHeader';
import {
  ReferenceFeedbackListPaneProps,
  ScorecardFeedbackListPaneProps,
} from '../../types/feedbackDetailTypes';
import { Rows } from '../../styles/styles';
import { Alert, CircularProgress, Stack, Typography } from '@mui/material';
import {
  useQueryGetCompletedAndDraftReferencesForHiringRequest,
  useQueryPendingReferencesForHiringRequest,
} from '../../api/referenceQueries';
import { useQueryGetScorecardsForHiringRequest } from '../../api/scorecardQueries';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';
import { ListDataProps } from '../../types/feedbackListTypes';
import { FeedbackListCTAButtons } from './components/FeedbackListPaneButtons';
import { CandidateFeedbackModalTypes } from '../../enums';

export const ReferenceFeedbackListPane: React.FC<ReferenceFeedbackListPaneProps> = ({
  candidateId,
  onClose,
  onViewClick,
  onEditClick,
  roleTitle,
  titleText,
  modalType,
  onModalPaneChange,
}) => {
  const {
    data: completedDraftReferences,
    isLoading: isCompletedDraftReferencesLoading,
    error: completedDraftReferencesError,
  } = useQueryGetCompletedAndDraftReferencesForHiringRequest(candidateId, {
    enabled: modalType === CandidateFeedbackModalTypes.REFERENCE,
  });

  const {
    data: pendingReferences,
    isLoading: isPendingReferencesLoading,
    error: pendingReferencesError,
  } = useQueryPendingReferencesForHiringRequest(candidateId, {
    enabled: modalType === CandidateFeedbackModalTypes.REFERENCE,
  });

  const references =
    completedDraftReferences &&
    completedDraftReferences.concat(
      pendingReferences?.map((ref) => ({
        ...ref,
        created: ref.created instanceof Date ? ref.created.toISOString() : ref.created,
      }))
    );
  const isLoading = isCompletedDraftReferencesLoading || isPendingReferencesLoading;
  const error = completedDraftReferencesError || pendingReferencesError;
  const errorMessage = getAxiosErrorMessageByStatusCode(error);

  return (
    <>
      <FeedbackListPaneHeader
        count={references?.length}
        onClose={onClose}
        roleTitle={roleTitle}
        titleText={titleText}
        modalType={modalType}
      />
      <ListData
        onModalPaneChange={onModalPaneChange}
        onViewClick={onViewClick}
        onEditClick={onEditClick}
        isLoading={isLoading}
        modalType={modalType}
        data={references}
        errorMessage={errorMessage}
      />
    </>
  );
};

export const ScorecardFeedbackListPane: React.FC<ScorecardFeedbackListPaneProps> = ({
  onClose,
  onViewClick,
  onEditClick,
  roleTitle,
  titleText,
  modalType,
  hiringRequestId,
  onModalPaneChange,
}) => {
  const { data, isLoading, error } = useQueryGetScorecardsForHiringRequest(hiringRequestId, {
    enabled: modalType === CandidateFeedbackModalTypes.SCORECARD,
  });
  const errorMessage = getAxiosErrorMessageByStatusCode(error);

  return (
    <>
      <FeedbackListPaneHeader
        count={data?.length}
        onClose={onClose}
        roleTitle={roleTitle}
        titleText={titleText}
        modalType={modalType}
      />
      <ListData
        onModalPaneChange={onModalPaneChange}
        onViewClick={onViewClick}
        onEditClick={onEditClick}
        isLoading={isLoading}
        modalType={modalType}
        data={data}
        errorMessage={errorMessage}
      />
    </>
  );
};

export const ListData: React.FC<ListDataProps> = ({
  errorMessage,
  isLoading,
  modalType,
  onModalPaneChange,
  onViewClick,
  onEditClick,
  data,
}) => {
  if (isLoading) {
    return (
      <Stack spacing={3} alignItems="center" sx={{ mt: 4 }}>
        <CircularProgress
          data-testid={
            ATSConnectHiringRequestCandidateFeedbackTestIds.SCORECARDS_LIST_LOADING_SPINNER
          }
        />
      </Stack>
    );
  }

  if (errorMessage) {
    return (
      <Alert
        data-testid={ATSConnectHiringRequestCandidateFeedbackTestIds.SCORECARDS_LIST_ERROR}
        severity="error"
      >
        error
      </Alert>
    );
  }

  return (
    <>
      <Rows>
        {data?.length === 0 ? (
          <Typography
            data-testid={ATSConnectHiringRequestCandidateFeedbackTestIds.NO_SCORECARDS_MESSAGE}
          >
            There are no {modalType}s for this candidate.
          </Typography>
        ) : (
          <>
            {data &&
              data.map((feedback) => (
                <CandidateFeedbackRow
                  data={feedback}
                  key={feedback.id}
                  onEditClick={onEditClick}
                  modalType={modalType}
                  onViewClick={onViewClick}
                />
              ))}
          </>
        )}
      </Rows>
      <FeedbackListCTAButtons modalType={modalType} onModalPaneChange={onModalPaneChange} />
    </>
  );
};
